import { z } from "zod";
import { idSchema } from "./ids";
import { canonicalJson } from "./canonical-json";
import {
  anyValueSchema,
  COLUMN_VALUE_TYPES,
  type ColumnValueType,
  type Value,
  valueSchema,
  valueTypeOf,
} from "./value-types";

/**
 * A table of the firm's own facts and lookups (amendment A4, decision 116): typed columns with
 * stable IDs, key columns, and rows. Browser-safe: the web app and the servers check a table's
 * columns and a row's values with the same schemas. Storage lives in the API's data-tables module.
 */

/**
 * The closed set of column types: every type of the value-type catalog (`value-types`), so a cell
 * validates as a slot or an output of the same type does. Two keep the spellings columns were
 * stored under before the catalog, `yes_no` (yes/no) and `connection` (a login, one per row, for a
 * step whose account depends on the case, decision 134; its cells take workspace accounts only);
 * `valueTypeOf` reads them as the catalog's `yesNo` and `login`.
 */
export const COLUMN_TYPES = COLUMN_VALUE_TYPES;
export type ColumnType = ColumnValueType;

const name = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .refine((value) => !value.includes("\0"));

const option = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .refine((value) => !value.includes("\0"));

export const columnSchema = z
  .object({
    id: idSchema("dataColumn"),
    name,
    type: z.enum(COLUMN_TYPES),
    /** A choice column's options, in order. */
    options: z.array(option).min(1).max(100).optional(),
    /** The table a row column points into. */
    tableId: idSchema("dataTable").optional(),
  })
  .strict()
  .refine((column) => (column.type === "choice") === (column.options !== undefined))
  .refine((column) => (column.type === "row") === (column.tableId !== undefined))
  .refine((column) => !column.options || new Set(column.options).size === column.options.length);
export type Column = z.infer<typeof columnSchema>;

/** A table's columns and keys: names unique without regard to case, keys among the columns. */
export const tableShapeSchema = z
  .object({
    columns: z.array(columnSchema).min(1).max(100),
    keyColumnIds: z.array(idSchema("dataColumn")).max(5),
  })
  .strict()
  .refine(({ columns }) => new Set(columns.map((column) => column.id)).size === columns.length)
  .refine(
    ({ columns }) =>
      new Set(columns.map((column) => column.name.toLowerCase())).size === columns.length,
  )
  .refine(
    ({ columns, keyColumnIds }) =>
      new Set(keyColumnIds).size === keyColumnIds.length &&
      keyColumnIds.every((id) => columns.some((column) => column.id === id)),
  );
export type TableShape = z.infer<typeof tableShapeSchema>;

export const tableNameSchema = name;

/** The schema of one cell of a column: its type's value schema. An empty cell is absent from the row, never null. */
export function cellSchema(column: Column) {
  return valueSchema(valueTypeOf(column.type), { options: column.options });
}

/** A cell's value: what the column types hold. */
export type CellValue = Value;
export type RowValues = Record<string, CellValue>;

/**
 * A row's values checked against the table's columns: only its columns, each cell valid, every
 * key column filled. Answers the values as the schemas parsed them, or null.
 */
export function parseRowValues(shape: TableShape, values: unknown): RowValues | null {
  if (typeof values !== "object" || values === null || Array.isArray(values)) return null;
  const byId = new Map(shape.columns.map((column) => [column.id, column]));
  const parsed: RowValues = {};
  for (const [columnId, value] of Object.entries(values)) {
    const column = byId.get(columnId);
    if (!column) return null;
    if (value === undefined || value === null) continue;
    const cell = cellSchema(column).safeParse(value);
    if (!cell.success) return null;
    parsed[columnId] = cell.data;
  }
  if (shape.keyColumnIds.some((id) => parsed[id] === undefined)) return null;
  return parsed;
}

/**
 * The most bytes a row's key may take. Keys are held in a unique B-tree index, whose entries must
 * stay well under Postgres's limit of about 2,700 bytes, so a key of several long text cells is
 * refused before it is written.
 */
export const ROW_KEY_BYTES = 2000;

/** Whether a row's key, as `rowKey` makes it, is short enough to hold. */
export const keyFits = (key: string | null) =>
  key === null || new TextEncoder().encode(key).length <= ROW_KEY_BYTES;

/** The text that makes a row's key unique in its table; null for a table with no keys. */
export function rowKey(shape: TableShape, values: RowValues): string | null {
  if (shape.keyColumnIds.length === 0) return null;
  return canonicalJson(shape.keyColumnIds.map((id) => values[id] ?? null));
}

/** The most rows a pin, and so a release, carries. */
export const PIN_ROW_LIMIT = 10_000;

/** One row as a pin copies it: its ID, the revision it was at, and its values. */
const pinRowSchema = z
  .object({
    rowId: idSchema("dataTableRow"),
    revision: z.number().int().min(1),
    values: z.record(z.string(), anyValueSchema),
  })
  .strict();

/**
 * A table as a draft pins it (decision 117): its name, columns, keys and rows at one revision.
 * Cases on a release read a pinned table from the release, never the live table.
 */
export const pinContentSchema = z
  .object({
    name: tableNameSchema,
    columns: z.array(columnSchema).min(1).max(100),
    keyColumnIds: z.array(idSchema("dataColumn")).max(5),
    revision: z.number().int().min(1),
    rows: z.array(pinRowSchema).max(PIN_ROW_LIMIT),
  })
  .strict();
export type PinContent = z.infer<typeof pinContentSchema>;

/** A pin as a release document carries it: the table it copies, its content and its hash. */
export const pinnedTableSchema = pinContentSchema
  .extend({
    tableId: idSchema("dataTable"),
    hash: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .strict();
export type PinnedTable = z.infer<typeof pinnedTableSchema>;

/**
 * How to say what a member, a row or a login is named, where the caller knows. Without a name,
 * the ID stands, so a cell turned into text never loses what it held.
 */
export type CellNames = {
  member?: (userId: string) => string | null;
  row?: (rowId: string) => string | null;
  login?: (connectionId: string) => string | null;
};

const DURATION_WORDS: Record<string, [string, string]> = {
  minutes: ["minute", "minutes"],
  hours: ["hour", "hours"],
  businessDays: ["business day", "business days"],
  days: ["day", "days"],
  weeks: ["week", "weeks"],
};

/** `14:00` as "2 pm", `00:15` as "12:15 am". */
function timeWords(time: string): string {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  const hour = hours % 12 === 0 ? 12 : hours % 12;
  const half = hours < 12 ? "am" : "pm";
  return minutes === 0
    ? `${String(hour)} ${half}`
    : `${String(hour)}:${String(minutes).padStart(2, "0")} ${half}`;
}

/** How many minor units a currency's major unit has: 2 for USD, 0 for JPY. */
export function minorDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/**
 * A cell in plain words, as a person would say it ("2 pm", "$12.50", "3 business days", "yes"):
 * what a table line in the chat and the publish review say, and what a cell becomes when its
 * column turns into text.
 */
export function cellWords(column: Pick<Column, "type">, value: CellValue, names: CellNames = {}) {
  const type = valueTypeOf(column.type);
  // A scalar as it is; an object (a value of another type than its column says) as its JSON.
  const plain =
    typeof value === "string" || typeof value === "number" || typeof value === "boolean"
      ? String(value)
      : JSON.stringify(value);
  switch (type) {
    case "number":
    case "count":
      return plain;
    case "yesNo":
      return value === true ? "yes" : "no";
    case "amount": {
      const { minor, currency } = value as { minor: number; currency: string };
      const digits = minorDigits(currency);
      try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
          minor / 10 ** digits,
        );
      } catch {
        return `${(minor / 10 ** digits).toFixed(digits)} ${currency}`;
      }
    }
    case "date":
      return new Date(`${plain}T00:00:00Z`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    case "duration": {
      const { amount, unit } = value as { amount: number; unit: string };
      const [one, many] = DURATION_WORDS[unit] ?? [unit, unit];
      return `${String(amount)} ${amount === 1 ? one : many}`;
    }
    case "timeOfDay":
      return timeWords((value as { time: string }).time);
    case "member": {
      const { userId } = value as { userId: string };
      return names.member?.(userId) ?? userId;
    }
    case "row":
      return names.row?.(plain) ?? plain;
    case "login":
      return names.login?.(plain) ?? plain;
    default:
      return plain;
  }
}

/**
 * What a person calls a row: its key cells in words, joined by " · ", or else its first text
 * cell; null when it has neither.
 */
export function rowLabel(
  shape: TableShape | Pick<TableShape, "columns" | "keyColumnIds">,
  values: RowValues,
  names: CellNames = {},
): string | null {
  const byId = new Map(shape.columns.map((column) => [column.id, column]));
  const keys = shape.keyColumnIds.flatMap((id) => {
    const column = byId.get(id);
    const value = values[id];
    return column && value !== undefined ? [cellWords(column, value, names)] : [];
  });
  if (keys.length > 0 && keys.length === shape.keyColumnIds.length) return keys.join(" · ");
  const text = shape.columns.find(
    (column) => column.type === "text" && typeof values[column.id] === "string",
  );
  return text ? (values[text.id] as string) : null;
}

/**
 * The safe ways a column may change type (concept map item 9): anything becomes text, a count a
 * number, a number an amount with a currency, and a choice gains options (losing one only while no
 * row uses it). Null for any other change: that is a new column, filled from the old one.
 */
export type RetypeKind =
  | "same"
  | "to_text"
  | "count_to_number"
  | "number_to_amount"
  | "choice_options";

export function retypeKind(from: Column, to: Column): RetypeKind | null {
  if (from.type === to.type) {
    if (from.type === "choice") {
      const same =
        from.options?.length === to.options?.length &&
        (from.options ?? []).every((option, index) => to.options?.[index] === option);
      return same ? "same" : "choice_options";
    }
    return from.tableId === to.tableId ? "same" : null;
  }
  if (to.type === "text") return "to_text";
  if (from.type === "count" && to.type === "number") return "count_to_number";
  if (from.type === "number" && to.type === "amount") return "number_to_amount";
  return null;
}

/**
 * One cell carried across a safe retype, as the new column holds it; null when it cannot go (a
 * number with no currency, or below zero, for an amount; a choice option the new column lacks).
 */
export function convertCell(
  kind: RetypeKind,
  from: Column,
  to: Column,
  value: CellValue,
  options: { currency?: string | undefined; names?: CellNames } = {},
): CellValue | null {
  switch (kind) {
    case "same":
    case "count_to_number":
      return value;
    case "to_text": {
      const text = textValue.safeParse(cellWords(from, value, options.names));
      return text.success ? text.data : null;
    }
    case "number_to_amount": {
      const currency = options.currency;
      if (typeof value !== "number" || !currency) return null;
      const minor = Math.round(value * 10 ** minorDigits(currency));
      const amount = cellSchema(to).safeParse({ minor, currency });
      return amount.success ? amount.data : null;
    }
    case "choice_options":
      return typeof value === "string" && (to.options ?? []).includes(value) ? value : null;
  }
}

const textValue = valueSchema("text");

/** What the change list records of a pin: enough to name it, not its rows. */
export type PinSummary = { tableId: string; revision: number; rows: number; hash: string };
